import { useQuery, useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { listAllWorkflowExecutions, deleteWorkflowExecution, updateWorkflowExecution, BASE_URL, type ChatAttachment, type ChatCitation } from '@/services/DataService';
import { useAppStore } from '@/store/appStore';

export interface ChatHistoryToolCall {
  id: string;
  name: string;
  arguments: string;
  stream_to_ui?: boolean;
}

export interface ChatHistoryToolCallOutput {
  id: string;
  output: string;
}

export interface ChatHistoryItem {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  origin: string;
  created_at: string;
  updated_at: string;
  behaviour_action_state: Record<string, unknown>;
  tool_calls?: ChatHistoryToolCall[];
  tool_call_outputs?: ChatHistoryToolCallOutput[];
  files?: ChatAttachment[];
  citations?: ChatCitation[];
}

export interface WorkflowExecution {
  status: 'COMPLETED' | 'IN_PROGRESS' | 'FAILED' | 'APPROVAL_PENDING';
  id: number;
  title: string | null;
  chat_mode_id?: string | null;
  created_at: string;
  updated_at: string;
  workflow_execution_chat_history?: ChatHistoryItem[];
}

interface WorkflowExecutionsResponse {
  workflow_executions: WorkflowExecution[];
}

interface UseWorkflowExecutionsReturn {
  /**
   * Returns all workflow executions
   */
  executions: WorkflowExecution[] | undefined;
  
  /**
   * Returns the latest completed execution
   */
  getLatestCompletedExecution: () => WorkflowExecution | undefined;
  
  /**
   * Returns all completed executions sorted by created_at descending
   */
  getCompletedExecutions: () => WorkflowExecution[];
  
  /** Query status */
  isLoading: boolean;
  error: Error | null;
}

export const useWorkflowExecutions = (
  workflowInstanceId: string,
  enabled: boolean = false
): UseWorkflowExecutionsReturn => {
  const { data, isLoading, error } = useQuery<WorkflowExecutionsResponse>({
    queryKey: ['workflowExecutions', workflowInstanceId],
    queryFn: async () => {
      return await listAllWorkflowExecutions(BASE_URL, workflowInstanceId, {sort_by: 'id', sort_order: 'desc' });
    },
    enabled: enabled && Boolean(BASE_URL) && Boolean(workflowInstanceId),
    // Override global setting to refetch when window regains focus
    // Critical for updating UI state (e.g., showNewAnalysisButton) after user returns from idle
    refetchOnWindowFocus: true,
  });

  const executions = data?.workflow_executions;

  const getCompletedExecutions = () => {
    if (!executions) return [];

    return executions
      .filter(execution => execution.status === 'COMPLETED')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
  };

  const getLatestCompletedExecution = () => {
    const completedExecutions = getCompletedExecutions();
    return completedExecutions.length > 0 ? completedExecutions[0] : undefined;
  };

  return {
    executions,
    getLatestCompletedExecution,
    getCompletedExecutions,
    isLoading,
    error
  };
};

/**
 * Hook for infinite scroll pagination of workflow executions
 * Fetches 20 items per page with lazy loading support. Optionally filters
 * server-side by title substring — pass a debounced value from the caller so
 * the query key doesn't churn on every keystroke.
 */
export const useWorkflowExecutionsInfinite = (
  workflowInstanceId: string,
  enabled: boolean = false,
  search: string = ''
) => {
  const PAGE_SIZE = 20;

  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    error
  } = useInfiniteQuery({
    queryKey: ['workflowExecutionsInfinite', workflowInstanceId, search],
    queryFn: async ({ pageParam = 1, signal }) => {
      const response = await listAllWorkflowExecutions(BASE_URL, workflowInstanceId, {
        sort_by: 'id',
        sort_order: 'desc',
        page_size: PAGE_SIZE,
        page: pageParam,
        ...(search && { title: search }),
      }, signal);
      return {
        executions: response?.workflow_executions || [],
        currentPage: pageParam,
        hasMore: (response?.workflow_executions?.length || 0) === PAGE_SIZE
      };
    },
    getNextPageParam: (lastPage) => {
      return lastPage.hasMore ? lastPage.currentPage + 1 : undefined;
    },
    initialPageParam: 1,
    enabled: enabled && Boolean(BASE_URL) && Boolean(workflowInstanceId),
  });

  // Flatten all pages into a single array
  const executions = data?.pages.flatMap(page => page.executions) ?? [];

  return {
    executions,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    isRefetching,
    error
  };
};

// Simple UI helpers for Trigger screens and similar views
export const useHasAnyWorkflowExecutions = (opts?: { useCompletedOnly?: boolean }) => {
  const activeWorkflowInstance = useAppStore(state => state.activeWorkflowInstance);
  const workflowInstanceId = activeWorkflowInstance?.id ? String(activeWorkflowInstance.id) : '';
  const { executions, isLoading, error } = useWorkflowExecutions(
    workflowInstanceId,
    Boolean(workflowInstanceId)
  );

  const hasAny = (executions?.length ?? 0) > 0;
  const hasCompleted = executions?.some(execution => execution.status === 'COMPLETED') ?? false;

  const useCompletedOnly = opts?.useCompletedOnly ?? false;
  const hasPastExecutions = useCompletedOnly ? hasCompleted : hasAny;

  return { hasPastExecutions, isLoading, error };
};

export const useDeleteWorkflowExecution = (workflowInstanceId: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (workflowExecutionId: string) =>
      deleteWorkflowExecution(workflowInstanceId, workflowExecutionId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workflowExecutionsInfinite', workflowInstanceId],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflowExecutions', workflowInstanceId],
      });
    },
  });
};

/**
 * Rename a workflow execution. `title === ''` or `null` clears the title back
 * to auto-title fallback behavior. Invalidates both the paginated sidebar
 * list and the flat list so any consumer re-fetches. The per-execution
 * chat-history cache does not carry the title, so it is not invalidated here.
 */
export const useUpdateWorkflowExecution = (workflowInstanceId: string) => {
  const queryClient = useQueryClient();

  return useMutation<WorkflowExecution, Error, { workflowExecutionId: string; title: string | null }>({
    mutationFn: ({ workflowExecutionId, title }) =>
      updateWorkflowExecution(workflowInstanceId, workflowExecutionId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['workflowExecutionsInfinite', workflowInstanceId],
      });
      queryClient.invalidateQueries({
        queryKey: ['workflowExecutions', workflowInstanceId],
      });
    },
  });
};
